import * as React from 'react'

import styled from '@theme/styled-components'
import Theme from '@theme/Theme'
import useI18n from '@store/i18n/useI18n'
import useDebounce from '@utils/useDebounce'

import Loader from 'react-loader-spinner'
import apis from '@app/api/api'
import Logger from '@utils/Logger'

import BusinessSelectModal from '@components/select/businessSelectModal'

interface Props {
  selected: Option[]
  setSelected: (b: Option[]) => void
  regionList: Option[]
  placeholder: string
}

const BusinessesMultiSelect = ({ selected, setSelected, regionList, placeholder }: Props) => {
  const i18n = useI18n()

  const [txt, bounced, setText] = useDebounce('')
  const [selectOpen, setSelectOpen] = React.useState<boolean>(false)
  const [businesses, setBusinesses] = React.useState<Option[]>([])
  const [selectedRegion, setSelectedRegion] = React.useState<Option>(regionList[0])
  const [screenStatus, setScreenStatus] = React.useState<ScreenStatus>('loading')

  React.useEffect(() => {
    fetchBusinesses(bounced, selectedRegion.value)
  }, [])

  React.useEffect(() => {
    fetchBusinesses(bounced, selectedRegion.value)
  }, [bounced, selectedRegion])

  const fetchBusinesses = (text: string, region: string) => {
    setScreenStatus('loading')
    apis.admin.list
      .businesses({ text, region })
      .then((res) => {
        setBusinesses(res.options)
        setScreenStatus('fetched')
      })
      .catch((err) => {
        setScreenStatus('error')
        Logger.error('Une erreur est survenue lors de la récuperation des affaires :', err)
      })
  }

  return (
    <>
      <Container>
        <SelectContainer selectOpen={selectOpen}>
          {screenStatus === 'loading' ? (
            <StatusContainer>
              <Loader type="ThreeDots" color={Theme.colors.classicButtonLoader} height={10} />
            </StatusContainer>
          ) : screenStatus === 'error' ? (
            <StatusContainer>{i18n.t('components.detailsChantier.error')}</StatusContainer>
          ) : selected.length !== 0 ? (
            <AllSelectedContainer>
              {selected.map((s) => (
                <SelectedContainer key={s.value}>
                  <SelectedLabel>{s.label}</SelectedLabel>
                </SelectedContainer>
              ))}
            </AllSelectedContainer>
          ) : (
            placeholder
          )}
        </SelectContainer>
      </Container>
      {selectOpen && (
        <BusinessSelectModal
          placeholder={placeholder}
          setSelectOpen={setSelectOpen}
          regionList={regionList}
          selectedRegion={selectedRegion}
          setSelectedRegion={setSelectedRegion}
          txt={txt}
          setText={setText}
          screenStatus={screenStatus}
          businesses={businesses}
          selected={selected}
          setSelected={setSelected}
        />
      )}
    </>
  )
}

export default BusinessesMultiSelect

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin: 5px 0px;
`
const SelectContainer = styled('div')<{ selectOpen: boolean; bordered?: boolean; isDisabled?: boolean }>`
  border: ${(props) => (props.bordered ? '2px solid red' : '1px solid #919191')};
  background: ${(props) => (props.isDisabled ? '#91919155' : '#FFFFFF')};
  ${(props) => props.theme.fonts.selectLibelle}
  box-sizing: border-box;
  align-items: center;
  padding: 0 10px;
  border-radius: 7px;
  min-height: 50px;
  display: flex;
  justify-content: space-between;
`
const StatusContainer = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  font-size: 13px;
`
const SelectedContainer = styled('div')`
  margin: 2px 5px 2px 0;
  display: flex;
  border-radius: 25px;
  align-items: center;
  justify-content: space-between;
  border: 1px solid ${(props) => props.theme.colors.primary_blue};
  background-color: ${(props) => props.theme.colors.filterBackgroundColor};
`
const SelectedLabel = styled('div')`
  display: flex;
  margin: 0 10px;
  align-items: center;
  justify-content: center;
`
const AllSelectedContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
`
